import React from 'react'
import { Helmet } from 'react-helmet-async'
import { SITE_URL } from '@/lib/seo'

export type SafeSEOProps = {
  title: string
  description: string
  url: string
  image?: string
  appendBrand?: boolean
  brandSuffix?: string
  maxTitle?: number
  noindex?: boolean
  nofollow?: boolean
  canonical?: string
  ogType?: 'website' | 'article'
  hreflang?: Record<string, string>
  keywords?: string[]
}

export function SafeSEO({
  title,
  description,
  url,
  image = '/img/og/open_graph.png',
  appendBrand = true,
  brandSuffix = ' | Joey',
  maxTitle = 60,
  noindex = false,
  nofollow = false,
  canonical,
  ogType = 'website',
  hreflang,
  keywords = [],
}: SafeSEOProps) {
  const base = (title ?? '').trim()
  const withBrand = appendBrand && !base.endsWith(brandSuffix) ? `${base}${brandSuffix}` : base
  const fullTitle = withBrand.length <= maxTitle ? withBrand : base
  const canon = (canonical ?? url).replace(/(?<!:)\/*$/, '')
  const robots = `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`
  const absoluteImage = image && !image.startsWith('http') ? `${SITE_URL}${image}` : image

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canon} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canon} />
      <meta property="og:image" content={absoluteImage} />

      {/* GC note: we dont have a Twitter account 
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} /> */}

      {hreflang &&
        Object.entries(hreflang).map(([lang, href]) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={href} />
        ))}
    </Helmet>
  )
}

